import { createSlice } from '@reduxjs/toolkit';

// Nights kept per customer for the session; older entries are evicted past this.
export const NIGHT_CACHE_LIMIT = 14;
export const CUSTOMER_CACHE_LIMIT = 5;

const emptyList = { items: [], loading: false, error: null };

const customerState = () => ({
  overview: { data: null, loading: false, error: null },
  activity: { date: null, data: null, loading: false, error: null },
  night: { date: null, loading: false, error: null },
  trends: { key: null, request: null, data: null, loading: false, error: null },
  profile: { data: null, loading: false, error: null, saving: false, saveError: null },
  notes: { ...emptyList, loaded: false, page: 1, total: 0, posting: false, postError: null },
  interventions: { ...emptyList, loaded: false, decidingId: null, decideError: null },
  support: { ...emptyList, loaded: false },
  // Consent-gated reads: `locked` is the 403 coach_consent_required answer, kept apart from errors.
  intake: { data: null, loading: false, error: null, locked: false },
  checkIns: { months: {}, pending: [], error: null, locked: false },
});

const initialState = {
  active: false,
  caseload: { items: [], total: 0, page: 1, loading: false, error: null, query: '' },
  search: { items: [], loading: false, error: null, query: '' },
  customerId: null,
  // Night asked for with the selection, replayed if the selection beat the saga.
  requestedDate: null,
  customer: customerState(),
  // customerId -> date -> { data, fetchedAt }; survives customer switches.
  nightsByCustomer: {},
  // Week comparison cards: customerId -> night -> { date, series, fetchedAt, loading, error }.
  weeks: {},
};

// Least recently fetched go first; the night on screen is never evicted.
const trimNights = (nights, keep) => {
  const dates = Object.keys(nights)
    .filter((date) => date !== keep)
    .sort((a, b) => nights[a].fetchedAt - nights[b].fetchedAt || a.localeCompare(b));
  while (Object.keys(nights).length > NIGHT_CACHE_LIMIT) delete nights[dates.shift()];
};

const coachInsightsSlice = createSlice({
  name: 'coachInsights',
  initialState,
  reducers: {
    dashboardOpened: (state) => {
      state.active = true;
    },
    dashboardClosed: () => initialState,

    getCaseloadAction: (state, action) => {
      const { query = '', page = 1 } = action.payload || {};
      state.caseload.loading = true;
      state.caseload.error = null;
      state.caseload.query = query;
      if (page === 1) state.caseload.items = [];
    },
    getCaseloadSuccess: (state, action) => {
      if (!state.active) return;
      const { items, total, page } = action.payload;
      state.caseload.items = page === 1 ? items : [...state.caseload.items, ...items];
      state.caseload.total = total;
      state.caseload.page = page;
      state.caseload.loading = false;
    },
    getCaseloadFailure: (state, action) => {
      state.caseload.loading = false;
      state.caseload.error = action.payload;
    },

    searchAction: (state, action) => {
      state.search.query = action.payload;
      state.search.loading = Boolean(action.payload);
      state.search.error = null;
      if (!action.payload) state.search.items = [];
    },
    searchSuccess: (state, action) => {
      if (!state.active || action.payload.query !== state.search.query) return;
      state.search.items = action.payload.items;
      state.search.loading = false;
    },
    searchFailure: (state, action) => {
      if (!state.active || action.payload.query !== state.search.query) return;
      state.search.loading = false;
      state.search.error = action.payload.error;
    },

    customerSelected: (state, action) => {
      const { customerId, date } = action.payload;
      if (state.customerId === customerId) return;
      state.customerId = customerId;
      state.customer = customerState();
      state.customer.overview.loading = true;
      state.requestedDate = date ?? null;
    },
    getOverviewSuccess: (state, action) => {
      if (action.payload.customerId !== state.customerId) return;
      state.customer.overview = { data: action.payload.data, loading: false, error: null };
    },
    getOverviewFailure: (state, action) => {
      if (action.payload.customerId !== state.customerId) return;
      state.customer.overview.loading = false;
      state.customer.overview.error = action.payload.error;
    },

    nightRequested: (state, action) => {
      const { date, force } = action.payload;
      state.customer.night.date = date;
      state.customer.night.error = null;
      const cached = state.nightsByCustomer[state.customerId]?.[date];
      state.customer.night.loading = Boolean(force) || !cached;
    },
    getNightSuccess: (state, action) => {
      const { customerId, date, data, fetchedAt } = action.payload;
      const nights = (state.nightsByCustomer[customerId] ??= {});
      nights[date] = { data, fetchedAt };
      trimNights(nights, customerId === state.customerId ? state.customer.night.date : null);
      const customers = Object.keys(state.nightsByCustomer).filter((id) => id !== state.customerId);
      customers.sort(
        (a, b) =>
          Math.max(...Object.values(state.nightsByCustomer[a]).map((night) => night.fetchedAt)) -
          Math.max(...Object.values(state.nightsByCustomer[b]).map((night) => night.fetchedAt))
      );
      while (Object.keys(state.nightsByCustomer).length > CUSTOMER_CACHE_LIMIT) {
        delete state.nightsByCustomer[customers.shift()];
      }
      if (customerId === state.customerId && date === state.customer.night.date) {
        state.customer.night.loading = false;
      }
    },
    getNightFailure: (state, action) => {
      const { customerId, date, error } = action.payload;
      if (customerId !== state.customerId || date !== state.customer.night.date) return;
      state.customer.night.loading = false;
      state.customer.night.error = error;
    },

    activityRequested: (state, action) => {
      state.customer.activity = {
        date: action.payload.date,
        data: null,
        loading: true,
        error: null,
      };
    },
    getActivitySuccess: (state, action) => {
      const { customerId, date, data } = action.payload;
      if (customerId !== state.customerId || date !== state.customer.activity.date) return;
      state.customer.activity.data = data;
      state.customer.activity.loading = false;
    },
    getActivityFailure: (state, action) => {
      const { customerId, date, error } = action.payload;
      if (customerId !== state.customerId || date !== state.customer.activity.date) return;
      state.customer.activity.loading = false;
      state.customer.activity.error = error;
    },

    trendsRequested: (state, action) => {
      const { period, metrics, anchor, title } = action.payload;
      state.customer.trends = {
        key: `${period}|${metrics.join(',')}|${anchor}`,
        request: { period, metrics, anchor, title },
        data: null,
        loading: true,
        error: null,
      };
    },
    trendsClosed: (state) => {
      state.customer.trends = customerState().trends;
    },
    getTrendsSuccess: (state, action) => {
      const { customerId, key, data } = action.payload;
      if (customerId !== state.customerId || key !== state.customer.trends.key) return;
      state.customer.trends.data = data;
      state.customer.trends.loading = false;
    },
    getTrendsFailure: (state, action) => {
      const { customerId, key, error } = action.payload;
      if (customerId !== state.customerId || key !== state.customer.trends.key) return;
      state.customer.trends.loading = false;
      state.customer.trends.error = error;
    },

    panelRequested: (state, action) => {
      const { tab } = action.payload;
      if (tab === 'profile' && !state.customer.profile.data) state.customer.profile.loading = true;
      if (tab === 'notes' && !state.customer.notes.loaded) state.customer.notes.loading = true;
      if (tab === 'logs') {
        if (!state.customer.interventions.loaded) state.customer.interventions.loading = true;
        if (!state.customer.support.loaded) state.customer.support.loading = true;
      }
      if (tab === 'intake' && !state.customer.intake.data && !state.customer.intake.locked) {
        state.customer.intake.loading = true;
      }
    },
    getIntakeSuccess: (state, action) => {
      if (action.payload.customerId !== state.customerId) return;
      state.customer.intake = {
        data: action.payload.data,
        loading: false,
        error: null,
        locked: false,
      };
    },
    getIntakeFailure: (state, action) => {
      if (action.payload.customerId !== state.customerId) return;
      const { error, locked } = action.payload;
      state.customer.intake.loading = false;
      state.customer.intake.locked = Boolean(locked);
      state.customer.intake.error = locked ? null : error;
    },

    checkInsRequested: (state, action) => {
      const { months } = action.payload;
      const checkIns = state.customer.checkIns;
      if (checkIns.locked) return;
      checkIns.error = null;
      months.forEach((month) => {
        if (!checkIns.months[month] && !checkIns.pending.includes(month)) {
          checkIns.pending.push(month);
        }
      });
    },
    getCheckInsSuccess: (state, action) => {
      const { customerId, month, data } = action.payload;
      if (customerId !== state.customerId) return;
      const checkIns = state.customer.checkIns;
      checkIns.months[month] = { definitions: data.definitions, days: data.days };
      checkIns.pending = checkIns.pending.filter((m) => m !== month);
    },
    getCheckInsFailure: (state, action) => {
      const { customerId, month, error, locked } = action.payload;
      if (customerId !== state.customerId) return;
      const checkIns = state.customer.checkIns;
      checkIns.pending = checkIns.pending.filter((m) => m !== month);
      checkIns.locked = Boolean(locked);
      checkIns.error = locked ? null : error;
    },
    getProfileSuccess: (state, action) => {
      if (action.payload.customerId !== state.customerId) return;
      state.customer.profile.data = action.payload.data;
      state.customer.profile.loading = false;
      state.customer.profile.error = null;
    },
    getProfileFailure: (state, action) => {
      if (action.payload.customerId !== state.customerId) return;
      state.customer.profile.loading = false;
      state.customer.profile.error = action.payload.error;
    },
    saveProfileAction: (state) => {
      state.customer.profile.saving = true;
      state.customer.profile.saveError = null;
    },
    saveProfileSuccess: (state, action) => {
      if (action.payload.customerId !== state.customerId) return;
      state.customer.profile.data = action.payload.data;
      state.customer.profile.saving = false;
    },
    saveProfileFailure: (state, action) => {
      if (action.payload.customerId !== state.customerId) return;
      state.customer.profile.saving = false;
      state.customer.profile.saveError = action.payload.error;
    },

    loadMoreNotesAction: (state) => {
      state.customer.notes.loading = true;
    },
    getNotesSuccess: (state, action) => {
      const { customerId, items, total, page } = action.payload;
      if (customerId !== state.customerId) return;
      // Pages are newest-first, so later pages append older notes.
      state.customer.notes.items = page > 1 ? [...state.customer.notes.items, ...items] : items;
      state.customer.notes.page = page;
      state.customer.notes.total = total;
      state.customer.notes.loaded = true;
      state.customer.notes.loading = false;
      state.customer.notes.error = null;
    },
    getNotesFailure: (state, action) => {
      if (action.payload.customerId !== state.customerId) return;
      state.customer.notes.loading = false;
      state.customer.notes.error = action.payload.error;
    },
    createNoteAction: (state) => {
      state.customer.notes.posting = true;
      state.customer.notes.postError = null;
    },
    createNoteSuccess: (state, action) => {
      if (action.payload.customerId !== state.customerId) return;
      state.customer.notes.items.unshift(action.payload.note);
      state.customer.notes.total += 1;
      state.customer.notes.posting = false;
    },
    createNoteFailure: (state, action) => {
      if (action.payload.customerId !== state.customerId) return;
      state.customer.notes.posting = false;
      state.customer.notes.postError = action.payload.error;
    },

    getInterventionsSuccess: (state, action) => {
      if (action.payload.customerId !== state.customerId) return;
      state.customer.interventions.items = action.payload.items;
      state.customer.interventions.loaded = true;
      state.customer.interventions.loading = false;
      state.customer.interventions.error = null;
    },
    getInterventionsFailure: (state, action) => {
      if (action.payload.customerId !== state.customerId) return;
      state.customer.interventions.loading = false;
      state.customer.interventions.error = action.payload.error;
    },
    decideInterventionAction: (state, action) => {
      state.customer.interventions.decidingId = action.payload.interventionId;
      state.customer.interventions.decideError = null;
    },
    decideInterventionSuccess: (state, action) => {
      if (action.payload.customerId !== state.customerId) return;
      const { intervention } = action.payload;
      const index = state.customer.interventions.items.findIndex((i) => i.id === intervention.id);
      if (index >= 0) state.customer.interventions.items[index] = intervention;
      state.customer.interventions.decidingId = null;
    },
    decideInterventionFailure: (state, action) => {
      if (action.payload.customerId !== state.customerId) return;
      state.customer.interventions.decidingId = null;
      state.customer.interventions.decideError = action.payload.error;
    },

    getSupportSuccess: (state, action) => {
      if (action.payload.customerId !== state.customerId) return;
      state.customer.support.items = action.payload.items;
      state.customer.support.loaded = true;
      state.customer.support.loading = false;
      state.customer.support.error = null;
    },
    getSupportFailure: (state, action) => {
      if (action.payload.customerId !== state.customerId) return;
      state.customer.support.loading = false;
      state.customer.support.error = action.payload.error;
    },

    // Week comparison cards (the seven nights ending on the open night).
    weekRequested: (state, action) => {
      const { customerId, date } = action.payload;
      // Only the open customer's weeks are kept; each is one small read.
      Object.keys(state.weeks).forEach((id) => id !== customerId && delete state.weeks[id]);
      const weeks = (state.weeks[customerId] ??= {});
      // A re-read keeps the card on screen; only a first read has nothing to show.
      weeks[date] = {
        ...weeks[date],
        date,
        series: weeks[date]?.series ?? null,
        loading: true,
        error: null,
      };
    },
    getWeekSuccess: (state, action) => {
      const { customerId, date, series, fetchedAt } = action.payload;
      const week = state.weeks[customerId]?.[date];
      if (!week) return;
      week.series = series;
      week.fetchedAt = fetchedAt;
      week.loading = false;
    },
    getWeekFailure: (state, action) => {
      const { customerId, date, error } = action.payload;
      const week = state.weeks[customerId]?.[date];
      if (!week) return;
      week.loading = false;
      // A failed re-read leaves the good card; the error shows only with nothing else to show.
      if (!week.series) week.error = error;
    },
  },
});

export const {
  dashboardOpened,
  dashboardClosed,
  getCaseloadAction,
  getCaseloadSuccess,
  getCaseloadFailure,
  searchAction,
  searchSuccess,
  searchFailure,
  customerSelected,
  getOverviewSuccess,
  getOverviewFailure,
  nightRequested,
  getNightSuccess,
  getNightFailure,
  activityRequested,
  getActivitySuccess,
  getActivityFailure,
  trendsRequested,
  trendsClosed,
  getTrendsSuccess,
  getTrendsFailure,
  panelRequested,
  getProfileSuccess,
  getProfileFailure,
  saveProfileAction,
  saveProfileSuccess,
  saveProfileFailure,
  loadMoreNotesAction,
  getNotesSuccess,
  getNotesFailure,
  createNoteAction,
  createNoteSuccess,
  createNoteFailure,
  getInterventionsSuccess,
  getInterventionsFailure,
  decideInterventionAction,
  decideInterventionSuccess,
  decideInterventionFailure,
  getSupportSuccess,
  getSupportFailure,
  getIntakeSuccess,
  getIntakeFailure,
  checkInsRequested,
  getCheckInsSuccess,
  getCheckInsFailure,
  weekRequested,
  getWeekSuccess,
  getWeekFailure,
} = coachInsightsSlice.actions;

export { coachInsightsSlice };
